import { byCssClass } from "../common/dom-util.js";

const SearchBar = {
  input: "portal-searchbar__input",
  icon: "portal-searchbar__icon",
  wrapper: "portal-searchbar",
  extended: "portal-searchbar--expanded",
  manualClosed: "portal-searchbar--manual-closed",
  formRecordColumn: "form-record__column",
};

const PageContent = {
  main: "base-wrapper",
};

const Toolbar = {
  main: "toolbar",
  searching: "toolbar--searching",
};

export const initSearchbar = () => {
  addEventListenerForSearchBar();
  observeSearchBar();
};

const addEventListenerForSearchBar = () => {
  document.addEventListener("click", handleDocumentClick);
  document.addEventListener("keydown", handleDocumentClick);
};

const checkAndSetSearchBarClass = () => {
  const searchBar = byCssClass(SearchBar.input);
  const searchBarWrapper = byCssClass(SearchBar.wrapper);
  const toolbar = byCssClass(Toolbar.main);
  if (
    !(searchBar instanceof HTMLInputElement) ||
    !(searchBarWrapper instanceof HTMLElement) ||
    !(toolbar instanceof HTMLElement)
  ) {
    return;
  }

  if (searchBar.value.trim() !== "" && !searchBarWrapper.classList.contains(SearchBar.manualClosed)) {
    searchBarWrapper.classList.add(SearchBar.extended);
    toolbar.classList.add(Toolbar.searching);
  }
};

const observeSearchBar = () => {
  const parentElement = byCssClass(PageContent.main);

  if (!(parentElement instanceof HTMLElement)) {
    return;
  }

  const observer = new MutationObserver((mutationsList) => {
    for (const mutation of mutationsList) {
      if (mutation.type === "childList") {
        const searchBarWrapper = byCssClass(SearchBar.wrapper);
        if (searchBarWrapper) {
          checkAndSetSearchBarClass();
          break;
        }
      }
    }
  });

  observer.observe(parentElement, { childList: true, subtree: true });
};

/**
 * @param {Event} event
 */
const handleDocumentClick = (event) => {
  const searchBar = byCssClass(SearchBar.input);
  const searchBarIcon = byCssClass(SearchBar.icon);
  const searchBarWrapper = byCssClass(SearchBar.wrapper);
  const toolbar = byCssClass(Toolbar.main);

  if (
    !(searchBar instanceof HTMLInputElement) ||
    !(searchBarIcon instanceof HTMLElement) ||
    !(searchBarWrapper instanceof HTMLElement) ||
    !(toolbar instanceof HTMLElement)
  ) {
    return;
  }

  const isPointer = event instanceof PointerEvent && event.target instanceof HTMLElement;
  const isKeyboard =
    event instanceof KeyboardEvent &&
    event.target instanceof HTMLElement &&
    (event.key === "Enter" || event.key === "Escape" || event.key === " " || event.key === "Spacebar");

  if (!isPointer && !isKeyboard) {
    return;
  }

  const isOpen = searchBarWrapper.classList.contains(SearchBar.extended);
  const isManualClose = isManualClosed(event, searchBarIcon);
  const isOutside = isClickOrKeydownOutsideSearchBar(event, searchBar, searchBarIcon, searchBarWrapper);

  if (isKeyboard && isOutside) {
    return;
  }

  if (isOutside && searchBar.value.trim() === "") {
    closeSearchbar(searchBarWrapper, toolbar);
    return;
  }

  if (isOpen && isManualClose) {
    closeSearchbar(searchBarWrapper, toolbar);
    searchBarWrapper.classList.add(SearchBar.manualClosed);
  } else {
    openSearchbar(searchBarWrapper, toolbar);
  }

  if (searchBarWrapper.classList.contains(SearchBar.extended)) {
    searchBar.focus();
    searchBarWrapper.classList.remove(SearchBar.manualClosed);
  }
};

/**
 *
 * @param {PointerEvent | KeyboardEvent} event
 * @param {HTMLElement}  searchBarIcon
 * @returns {boolean}
 */
const isManualClosed = (event, searchBarIcon) => {
  return (
    (event instanceof KeyboardEvent && event.key === "Escape") ||
    (event instanceof PointerEvent && event.target === searchBarIcon)
  );
};

/**
 * @param {PointerEvent | KeyboardEvent} event
 * @param {HTMLInputElement} searchBar
 * @param {HTMLElement} searchBarIcon
 * @param {HTMLElement} searchBarWrapper
 */
const isClickOrKeydownOutsideSearchBar = (event, searchBar, searchBarIcon, searchBarWrapper) => {
  return !(event.target === searchBar || event.target === searchBarIcon || event.target === searchBarWrapper);
};

/**
 * Close the search bar programmatically.
 * @param {HTMLElement} searchBarWrapper
 * @param {HTMLElement} toolbar
 */
const closeSearchbar = (searchBarWrapper, toolbar) => {
  searchBarWrapper?.classList.remove(SearchBar.extended);
  toolbar?.classList.remove(Toolbar.searching);
  searchBarWrapper?.setAttribute("aria-expanded", "false");
};

/**
 * Open the search bar programmatically.
 * @param {HTMLElement} searchBarWrapper
 * @param {HTMLElement} toolbar
 */
const openSearchbar = (searchBarWrapper, toolbar) => {
  searchBarWrapper?.classList.add(SearchBar.extended);
  toolbar?.classList.add(Toolbar.searching);
  searchBarWrapper?.setAttribute("aria-expanded", "true");
};
